import { useMemo } from 'react';
import { useGetEventTasksForLecturer } from '../../../common/api/endpoints/tasks/tasks';
import { useAuthStore } from '../../../common/auth/store';
import { useCalendarBadges } from './useCalendarBadges';

/**
 * Event-task and mandate data for slot sidebar components (create/edit slot).
 * Does not fetch appointment slots — the calendar owns the windowed slots query.
 */
export const useEventCalendarData = () => {
  const userId = useAuthStore((state) => state.user.id);

  const {
    mandates,
    moduleMap: mandateModuleMap,
    planningGaps,
    totalMissingSlots,
  } = useCalendarBadges();

  const { data: tasksData, isPending: tasksLoading } = useGetEventTasksForLecturer(
    userId,
    { include: 'event_config' },
    {
      query: {
        enabled: !!userId,
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  const allTasks = useMemo(() => (tasksData?.data?.data || []).filter((t) => !t.deletedAt), [tasksData]);

  const moduleMap = useMemo(() => {
    const map = { ...mandateModuleMap };

    allTasks.forEach((t) => {
      if (t.moduleId && !map[t.moduleId]) {
        map[t.moduleId] = t.moduleId;
      }
    });

    return map;
  }, [mandateModuleMap, allTasks]);

  const taskMap = useMemo(() => {
    const map = {};
    allTasks.forEach((t) => {
      map[t.id] = { title: t.title, moduleId: t.moduleId };
    });
    return map;
  }, [allTasks]);

  const taskVersionMap = useMemo(() => {
    const map = {};
    allTasks.forEach((t) => {
      map[t.id] = t.version ?? null;
    });
    return map;
  }, [allTasks]);

  return {
    allTasks,
    mandates,
    planningGaps,
    totalMissingSlots,
    taskMap,
    moduleMap,
    taskVersionMap,
    tasksLoading,
  };
};
